import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useInviteAuth } from './InviteAuthContext';
import { useInviteTheme } from './InviteThemeContext';
import API_BASE_URL from '../../config';
import { InviteCardGenerator } from '../../components/InviteCardGenerator';
import './InviteCampaignDetail.css';
import {
    RefreshCw,
    Loader2,
    Send,
    CheckCircle2,
    XCircle,
    Clock,
    ArrowLeft,
    AlertCircle,
    Check,
    Download,
    FileSpreadsheet,
    Users,
    Utensils,
    Home,
} from 'lucide-react';

interface Recipient {
    _id: string;
    name: string;
    phone: string;
    email?: string;
    customFields?: {
        salutation?: string;
        relationship?: string;
        familySide?: string;
    };
    rsvpStatus: 'pending' | 'accepted' | 'declined' | 'maybe';
    attendeesCount?: number;
    adultsCount?: number;
    kidsCount?: number;
    dietaryPreference?: string;
    needsAccommodation?: boolean;
    arrivalDate?: string;
    transportDetails?: string;
    rsvpNote?: string;
    rsvpRespondedAt?: string;
    rsvpToken?: string;
    deliveryStatus?: Record<string, { status: string; sentAt?: string; error?: string }>;
}

interface Campaign {
    _id: string;
    eventName: string;
    eventType: string;
    eventDate: string;
    eventVenue: string;
    eventCity: string;
    status: string;
    channels: string[];
    mediaUrl?: string;
    mediaType?: 'image' | 'video' | 'none';
    hostName?: string;
    collectDietary?: boolean;
    collectAccommodation?: boolean;
    reminderEnabled?: boolean;
    reminderSent?: boolean;
    reminderSentAt?: string;
    sentCount: number;
    failedCount: number;
    totalRecipients?: number;
    rsvpAccepted: number;
    rsvpDeclined: number;
    rsvpMaybe: number;
    totalAttendingGuests?: number;
}

export const InviteCampaignDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { inviteToken } = useInviteAuth();
    const { theme } = useInviteTheme();

    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [resending, setResending] = useState(false);
    const [triggeringReminder, setTriggeringReminder] = useState(false);
    const [exportingRsvp, setExportingRsvp] = useState(false);
    const [rsvpFilter, setRsvpFilter] = useState<'all' | 'accepted' | 'declined' | 'maybe' | 'pending'>('all');
    const [resendNotice, setResendNotice] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<any>(null);

    const pollingRef = useRef<NodeJS.Timeout | null>(null);

    // ─── Fetch Campaign Details & Stats ───────────────────────────────────────────
    const fetchDetails = useCallback(async (showFullLoader = false) => {
        if (!id || !inviteToken) return;
        if (showFullLoader) setLoading(true);
        else setRefreshing(true);

        try {
            const [campaignRes, statsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/invite/campaigns/${id}`, {
                    headers: { Authorization: `Bearer ${inviteToken}` },
                }),
                fetch(`${API_BASE_URL}/api/invite/campaigns/${id}/stats`, {
                    headers: { Authorization: `Bearer ${inviteToken}` },
                }),
            ]);

            if (!campaignRes.ok) {
                throw new Error('Failed to retrieve invitation details');
            }

            const campaignData = await campaignRes.json();
            setCampaign(campaignData.campaign);
            setRecipients(campaignData.recipients || []);

            if (statsRes.ok) {
                const statsData = await statsRes.json();
                setStats(statsData.stats);
            }
            setError(null);
        } catch (err: any) {
            setError(err.message);
        } finally {
            if (showFullLoader) setLoading(false);
            setRefreshing(false);
        }
    }, [id, inviteToken]);

    // Initial Load
    useEffect(() => {
        fetchDetails(true);
    }, [fetchDetails]);

    // Check if there are any messages actively in-flight or waiting in the queue
    const hasPendingRecipients = recipients.some(r => {
        const statuses = Object.values(r.deliveryStatus || {}).map(s => s.status);
        return statuses.includes('sending') || statuses.includes('pending');
    });

    // Dispatch is active ONLY if the campaign is sending AND there are pending/sending recipients
    const isDispatchActive = (campaign?.status === 'sending') && hasPendingRecipients;
    const isResendDisabled = resending || isDispatchActive;

    useEffect(() => {
        if (isDispatchActive) {
            if (!pollingRef.current) {
                pollingRef.current = setInterval(() => {
                    fetchDetails(false);
                }, 3000);
            }
        } else {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
            setResending(false);
        }

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [isDispatchActive, fetchDetails]);

    // ─── Handle Resend to Failed Recipient Action ─────────────────────────────
    const handleResendFailed = async () => {
        if (resending || campaign?.status === 'sending') return;

        setResending(true);
        setError(null);
        setResendNotice('🚀 Dispatch initiated in background! The queue worker is delivering messages.');

        try {
            const response = await fetch(`${API_BASE_URL}/api/invite/campaigns/${id}/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${inviteToken}`,
                },
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to start resend dispatch.');
            }

            // Immediately set campaign status to sending
            setCampaign(prev => prev ? { ...prev, status: 'sending' } : null);

            // Fetch fresh status right away
            setTimeout(() => fetchDetails(false), 1200);

        } catch (err: any) {
            setError(err.message);
            setResendNotice(null);
            setResending(false);
        }
    };

    // ─── Handle Manual 12-Hour Reminder Dispatch ─────────────────────────────
    const handleTriggerReminder = async () => {
        if (triggeringReminder) return;

        const acceptedCount = recipients.filter(r => r.rsvpStatus === 'accepted').length;
        const maybeCount = recipients.filter(r => r.rsvpStatus === 'maybe').length;
        const totalTarget = acceptedCount + maybeCount;

        if (totalTarget === 0) {
            alert('There are no Accepted or Tentative guests yet to send the 12-hour reminder to.');
            return;
        }

        if (!window.confirm(`Send 12-Hour Event Reminder to ${totalTarget} guests (${acceptedCount} Accepted, ${maybeCount} Tentative)?`)) {
            return;
        }

        setTriggeringReminder(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/invite/campaigns/${id}/reminder`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${inviteToken}`,
                },
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to dispatch 12-hour reminders.');
            }

            setResendNotice(`⏰ ${data.message}`);
            fetchDetails(false);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setTriggeringReminder(false);
        }
    };

    // ─── Handle Export RSVP Responses to Excel ────────────────────────────────
    const handleExportRsvp = async () => {
        if (exportingRsvp || !id || !inviteToken) return;
        setExportingRsvp(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/invite/campaigns/${id}/rsvp/export`, {
                headers: { Authorization: `Bearer ${inviteToken}` },
            });

            if (!response.ok) {
                throw new Error('Failed to export RSVP report.');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(campaign?.eventName || 'Event').replace(/[^a-z0-9]/gi, '_')}_RSVP_Report.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            alert('Error exporting RSVP: ' + err.message);
        } finally {
            setExportingRsvp(false);
        }
    };

    if (loading) {
        return (
            <div className="invite-spinner-container">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!campaign) {
        return (
            <div className={`invite-campaign-detail ${theme === 'light' ? 'invite-light-theme' : ''}`}>
                <div className="campaign-detail-card" style={{ textAlign: 'center', padding: '3rem' }}>
                    <AlertCircle size={40} color="#F87171" style={{ marginBottom: '1rem' }} />
                    <h3 className="campaign-detail-card-title">Invitation not found</h3>
                    <p className="campaign-detail-card-subtitle" style={{ marginBottom: '1.5rem' }}>The requested campaign does not exist or has been deleted.</p>
                    <Link to="/invite/dashboard" className="back-to-dashboard-link">
                        <ArrowLeft size={16} /> Back to Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    // Compute pending count
    const totalRecipients = stats?.totalRecipients || recipients.length || campaign.totalRecipients || 0;
    const pendingCount = Math.max(0, totalRecipients - (campaign.sentCount || 0) - (campaign.failedCount || 0));

    // Filter recipients by current tab
    const filteredRecipients = recipients.filter(r => {
        if (rsvpFilter === 'all') return true;
        if (rsvpFilter === 'accepted') return r.rsvpStatus === 'accepted';
        if (rsvpFilter === 'declined') return r.rsvpStatus === 'declined';
        if (rsvpFilter === 'maybe') return r.rsvpStatus === 'maybe';
        if (rsvpFilter === 'pending') return !r.rsvpStatus || r.rsvpStatus === 'pending';
        return true;
    });

    const isLightTheme = theme === 'light';

    return (
        <div className={`invite-campaign-detail ${isLightTheme ? 'invite-light-theme' : ''}`}>
            {/* Header section with back button */}
            <div className="campaign-detail-header">
                <Link to="/invite/dashboard" className="back-to-dashboard-link">
                    <ArrowLeft size={16} /> Back to Dashboard
                </Link>

                <div className="campaign-title-row">
                    <h2 className="campaign-main-title">{campaign.eventName}</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <span className={`status-badge ${campaign.status}`} style={{ textTransform: 'capitalize', padding: '4px 10px', borderRadius: '8px', fontWeight: 700, fontSize: '0.8rem' }}>
                            {campaign.status === 'sending' ? '⏳ Sending...' : campaign.status}
                        </span>
                        <button
                            onClick={() => fetchDetails(false)}
                            disabled={refreshing}
                            className="btn-refresh-status"
                        >
                            <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                            {refreshing ? 'Updating...' : 'Refresh Status'}
                        </button>
                    </div>
                </div>

                <p className="campaign-meta-subtitle">
                    📅 {new Date(campaign.eventDate).toLocaleString()} · 📍 {campaign.eventVenue}, {campaign.eventCity}
                </p>

                {/* Resend Notice Banner */}
                {resendNotice && (
                    <div style={{ background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.35)', padding: '0.85rem 1.25rem', borderRadius: '12px', marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: isLightTheme ? '#059669' : '#34D399', fontWeight: 600, fontSize: '0.9rem' }}>
                        <Check size={18} />
                        <span>{resendNotice}</span>
                    </div>
                )}

                {/* Error Banner */}
                {error && (
                    <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.35)', padding: '0.85rem 1.25rem', borderRadius: '12px', marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: isLightTheme ? '#DC2626' : '#FCA5A5', fontWeight: 600, fontSize: '0.9rem' }}>
                        <AlertCircle size={18} />
                        <span>{error}</span>
                    </div>
                )}

                {campaign.status === 'draft' && (
                    <div style={{ background: isLightTheme ? '#FAF5FF' : 'rgba(139, 92, 246, 0.12)', border: isLightTheme ? '1.5px dashed #C084FC' : '1px dashed rgba(139, 92, 246, 0.4)', padding: '1.25rem', borderRadius: '14px', marginTop: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                        <div>
                            <h4 style={{ color: isLightTheme ? '#7C3AED' : '#C084FC', margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>📁 Invitation Draft Mode</h4>
                            <p style={{ color: isLightTheme ? '#64748B' : '#94A3B8', margin: '0.25rem 0 0', fontSize: '0.82rem' }}>This invitation has not been launched yet. You can resume editing and invite guests.</p>
                        </div>
                        <Link to={`/invite/new?edit=${campaign._id}`} style={{ margin: 0, textDecoration: 'none', padding: '8px 18px', background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)', color: 'white', borderRadius: '10px', fontWeight: 700, fontSize: '0.85rem', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)' }}>
                            ✏️ Edit & Launch Draft
                        </Link>
                    </div>
                )}
            </div>

            {/* Aggregated Stats Cards */}
            {stats && (
                <div style={{ marginBottom: '2.5rem' }}>
                    <div className="stats-grid-row">
                        <div className="kpi-stat-card">
                            <span className="kpi-stat-icon">👥</span>
                            <div className="kpi-stat-info">
                                <h3 className="kpi-stat-value">{stats.totalRecipients || 0}</h3>
                                <p className="kpi-stat-label">Total Invitees</p>
                            </div>
                        </div>

                        <div className="kpi-stat-card headcount">
                            <span className="kpi-stat-icon">🎉</span>
                            <div className="kpi-stat-info">
                                <h3 className="kpi-stat-value headcount">{stats.totalAttendingGuests || stats.rsvpAccepted || 0}</h3>
                                <p className="kpi-stat-label" style={{ color: isLightTheme ? '#7C3AED' : '#E9D5FF', fontWeight: 700 }}>Confirmed Headcount</p>
                            </div>
                        </div>

                        <div className="kpi-stat-card" style={{ borderLeft: '3px solid #10B981' }}>
                            <span className="kpi-stat-icon">✅</span>
                            <div className="kpi-stat-info">
                                <h3 className="kpi-stat-value accepted">{stats.rsvpAccepted || 0}</h3>
                                <p className="kpi-stat-label">Accepted Responses</p>
                            </div>
                        </div>

                        <div className="kpi-stat-card" style={{ borderLeft: '3px solid #F59E0B' }}>
                            <span className="kpi-stat-icon">❓</span>
                            <div className="kpi-stat-info">
                                <h3 className="kpi-stat-value maybe">{stats.rsvpMaybe || 0}</h3>
                                <p className="kpi-stat-label">Tentative / Maybe</p>
                            </div>
                        </div>

                        <div className="kpi-stat-card" style={{ borderLeft: '3px solid #EF4444' }}>
                            <span className="kpi-stat-icon">❌</span>
                            <div className="kpi-stat-info">
                                <h3 className="kpi-stat-value declined">{stats.rsvpDeclined || 0}</h3>
                                <p className="kpi-stat-label">Declined</p>
                            </div>
                        </div>

                        <div className="kpi-stat-card" style={{ borderLeft: '3px solid #64748B' }}>
                            <span className="kpi-stat-icon">⏳</span>
                            <div className="kpi-stat-info">
                                <h3 className="kpi-stat-value pending">{stats.rsvpPending || 0}</h3>
                                <p className="kpi-stat-label">Awaiting Reply</p>
                            </div>
                        </div>
                    </div>

                    {/* Catering & Guest Logistics Overview Bar */}
                    <div className="catering-logistics-bar">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Utensils size={18} color="#7C3AED" />
                                <div>
                                    <span className="catering-group-label">Dietary Choices</span>
                                    <div style={{ display: 'flex', gap: '6px', marginTop: '3px', flexWrap: 'wrap' }}>
                                        {stats.dietaryStats?.vegetarian > 0 && <span style={{ fontSize: '0.78rem', background: 'rgba(16, 185, 129, 0.15)', color: isLightTheme ? '#059669' : '#34D399', padding: '2px 8px', borderRadius: '12px', fontWeight: 700 }}>🌱 {stats.dietaryStats.vegetarian} Veg</span>}
                                        {stats.dietaryStats?.['non-vegetarian'] > 0 && <span style={{ fontSize: '0.78rem', background: 'rgba(239, 68, 68, 0.15)', color: isLightTheme ? '#DC2626' : '#F87171', padding: '2px 8px', borderRadius: '12px', fontWeight: 700 }}>🍗 {stats.dietaryStats['non-vegetarian']} Non-Veg</span>}
                                        {stats.dietaryStats?.jain > 0 && <span style={{ fontSize: '0.78rem', background: 'rgba(245, 158, 11, 0.15)', color: isLightTheme ? '#D97706' : '#FBBF24', padding: '2px 8px', borderRadius: '12px', fontWeight: 700 }}>🥗 {stats.dietaryStats.jain} Jain</span>}
                                        {stats.dietaryStats?.vegan > 0 && <span style={{ fontSize: '0.78rem', background: 'rgba(56, 189, 248, 0.15)', color: isLightTheme ? '#0284C7' : '#38BDF8', padding: '2px 8px', borderRadius: '12px', fontWeight: 700 }}>🥑 {stats.dietaryStats.vegan} Vegan</span>}
                                        {stats.dietaryStats?.eggetarian > 0 && <span style={{ fontSize: '0.78rem', background: 'rgba(217, 119, 6, 0.15)', color: isLightTheme ? '#B45309' : '#FDE047', padding: '2px 8px', borderRadius: '12px', fontWeight: 700 }}>🥚 {stats.dietaryStats.eggetarian} Eggetarian</span>}
                                        {(!stats.dietaryStats || Object.values(stats.dietaryStats).reduce((a: any, b: any) => a + b, 0) === 0) && (
                                            <span style={{ fontSize: '0.78rem', color: isLightTheme ? '#64748B' : '#94A3B8' }}>No dietary choices submitted yet</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Home size={18} color="#0284C7" />
                                <div>
                                    <span className="catering-group-label">Accommodation</span>
                                    <span className="catering-headcount-text">
                                        🏨 {stats.accommodationCount || 0} Guests requested stay arrangements
                                    </span>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleExportRsvp}
                            disabled={exportingRsvp}
                            className="btn-export-excel"
                        >
                            <FileSpreadsheet size={16} />
                            <span>{exportingRsvp ? 'Generating Excel...' : 'Export RSVP Report (.xlsx)'}</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Delivery Status & Live Message Tracking */}
            <div className="campaign-detail-card" style={{ marginBottom: '2.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderBottom: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '1rem' }}>
                    <div>
                        <h3 className="campaign-detail-card-title">Message Delivery Status</h3>
                        <p className="campaign-detail-card-subtitle">
                            Channels: <strong style={{ color: isLightTheme ? '#7C3AED' : '#C084FC' }}>{campaign.channels.map(c => c.toUpperCase()).join(', ')}</strong>
                        </p>
                    </div>
                </div>

                {/* Delivery Counters Grid */}
                <div className="delivery-counters-grid">
                    <div className="delivery-counter-box">
                        <span className="delivery-counter-value">{totalRecipients}</span>
                        <span className="delivery-counter-label">Total Invitees</span>
                    </div>
                    <div className="delivery-counter-box" style={{ background: isLightTheme ? '#ECFDF5' : 'rgba(16, 185, 129, 0.12)', borderColor: isLightTheme ? '#A7F3D0' : 'rgba(16, 185, 129, 0.3)' }}>
                        <span className="delivery-counter-value" style={{ color: isLightTheme ? '#059669' : '#34D399' }}>{campaign.sentCount || 0}</span>
                        <span className="delivery-counter-label" style={{ color: isLightTheme ? '#059669' : '#34D399' }}>✅ Delivered</span>
                    </div>
                    <div className="delivery-counter-box" style={{ background: isLightTheme ? '#FFFBEB' : 'rgba(245, 158, 11, 0.12)', borderColor: isLightTheme ? '#FDE68A' : 'rgba(245, 158, 11, 0.3)' }}>
                        <span className="delivery-counter-value" style={{ color: isLightTheme ? '#D97706' : '#FBBF24' }}>{pendingCount}</span>
                        <span className="delivery-counter-label" style={{ color: isLightTheme ? '#D97706' : '#FBBF24' }}>⏳ In Progress</span>
                    </div>
                    <div className="delivery-counter-box" style={{ background: isLightTheme ? '#FEF2F2' : 'rgba(239, 68, 68, 0.12)', borderColor: isLightTheme ? '#FECACA' : 'rgba(239, 68, 68, 0.3)' }}>
                        <span className="delivery-counter-value" style={{ color: isLightTheme ? '#DC2626' : '#F87171' }}>{campaign.failedCount || 0}</span>
                        <span className="delivery-counter-label" style={{ color: isLightTheme ? '#DC2626' : '#F87171' }}>❌ Failed</span>
                    </div>
                </div>

                {/* Action Buttons: Resend to Failed / Retry */}
                {campaign.failedCount > 0 && (
                    <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <button
                            id="btn-resend-failed"
                            onClick={handleResendFailed}
                            disabled={isResendDisabled}
                            style={{
                                cursor: isResendDisabled ? 'not-allowed' : 'pointer',
                                background: isResendDisabled ? '#94A3B8' : 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                border: 'none',
                                borderRadius: '12px',
                                padding: '11px 26px',
                                color: '#FFF',
                                fontWeight: 800,
                                fontSize: '0.9rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                boxShadow: isResendDisabled ? 'none' : '0 4px 14px rgba(139, 92, 246, 0.35)',
                                opacity: isResendDisabled ? 0.7 : 1,
                                transition: 'all 0.2s',
                            }}
                        >
                            {isResendDisabled ? (
                                <>
                                    <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
                                    <span>⏳ Resending in background...</span>
                                </>
                            ) : (
                                <>
                                    <Send size={15} />
                                    <span>🔄 Resend to Failed ({campaign.failedCount})</span>
                                </>
                            )}
                        </button>

                        {isResendDisabled && (
                            <span style={{ fontSize: '0.82rem', color: isLightTheme ? '#D97706' : '#FBBF24', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontWeight: 600 }}>
                                <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                                Subsequent requests are disabled while dispatch is active.
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* ⏰ 12-Hour Guest Pre-Event Reminder Card */}
            <div className="campaign-detail-card" style={{
                marginBottom: '2.5rem',
                border: isLightTheme ? '1.5px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(139, 92, 246, 0.3)',
                background: isLightTheme ? '#FAF5FF' : 'rgba(17, 24, 39, 0.75)',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', borderBottom: isLightTheme ? '1px solid #E9D5FF' : '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '1.8rem' }}>⏰</span>
                        <div>
                            <h3 className="campaign-detail-card-title" style={{ color: isLightTheme ? '#5B21B6' : '#F8FAFC' }}>12-Hour Automated Pre-Event Reminder</h3>
                            <p className="campaign-detail-card-subtitle" style={{ color: isLightTheme ? '#6D28D9' : '#94A3B8' }}>
                                Automatically alerts confirmed &amp; tentative guests with event directions and countdown.
                            </p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {campaign.reminderSent ? (
                            <span style={{ background: isLightTheme ? '#D1FAE5' : 'rgba(16, 185, 129, 0.15)', color: isLightTheme ? '#065F46' : '#34D399', border: '1px solid rgba(16, 185, 129, 0.35)', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <CheckCircle2 size={14} /> Sent {campaign.reminderSentAt ? new Date(campaign.reminderSentAt).toLocaleTimeString() : ''}
                            </span>
                        ) : campaign.reminderEnabled ? (
                            <span style={{ background: isLightTheme ? '#EDE9FE' : 'rgba(139, 92, 246, 0.15)', color: isLightTheme ? '#6D28D9' : '#C084FC', border: '1px solid rgba(139, 92, 246, 0.35)', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 700 }}>
                                ⏰ Auto-Scheduled (12h Before)
                            </span>
                        ) : (
                            <span style={{ background: isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.05)', color: isLightTheme ? '#64748B' : '#94A3B8', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600 }}>
                                ⚪ Not Enabled
                            </span>
                        )}
                    </div>
                </div>

                {/* RSVP Audience & Media Overview */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1.25rem' }}>
                    <div style={{ background: isLightTheme ? '#FFFFFF' : 'rgba(15, 23, 42, 0.7)', border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px', padding: '1rem' }}>
                        <span style={{ fontSize: '0.75rem', color: isLightTheme ? '#7C3AED' : '#C084FC', fontWeight: 700, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Target Audience (RSVP Filtered)</span>
                        <div style={{ marginTop: '6px', fontSize: '0.95rem', fontWeight: 700, color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>
                            {recipients.filter(r => r.rsvpStatus === 'accepted').length} Accepted + {recipients.filter(r => r.rsvpStatus === 'maybe').length} Tentative
                        </div>
                        <span style={{ fontSize: '0.75rem', color: isLightTheme ? '#059669' : '#34D399', fontWeight: 600 }}>
                            = {recipients.filter(r => r.rsvpStatus === 'accepted' || r.rsvpStatus === 'maybe').length} Guests will receive reminder
                        </span>
                    </div>

                    <div style={{ background: isLightTheme ? '#FFFFFF' : 'rgba(15, 23, 42, 0.7)', border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px', padding: '1rem' }}>
                        <span style={{ fontSize: '0.75rem', color: isLightTheme ? '#7C3AED' : '#C084FC', fontWeight: 700, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Attached Media Trailer</span>
                        <div style={{ marginTop: '6px', fontSize: '0.9rem', fontWeight: 600, color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>
                            {campaign.mediaUrl ? (
                                <span style={{ color: isLightTheme ? '#0284C7' : '#38BDF8', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    {campaign.mediaType === 'video' ? '🎬 MP4 Video Attached' : '🖼️ Photo Card Attached'}
                                </span>
                            ) : (
                                <span style={{ color: isLightTheme ? '#64748B' : '#94A3B8' }}>No photo/video attached (Text only)</span>
                            )}
                        </div>
                        <span style={{ fontSize: '0.75rem', color: isLightTheme ? '#64748B' : '#94A3B8' }}>
                            Included in WhatsApp &amp; Telegram dispatches
                        </span>
                    </div>
                </div>

                {/* Reminder Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                    <button
                        onClick={handleTriggerReminder}
                        disabled={triggeringReminder}
                        style={{
                            background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '10px 22px',
                            color: '#FFF',
                            fontWeight: 800,
                            fontSize: '0.88rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            cursor: triggeringReminder ? 'not-allowed' : 'pointer',
                            opacity: triggeringReminder ? 0.7 : 1,
                            boxShadow: '0 4px 14px rgba(139, 92, 246, 0.35)',
                        }}
                    >
                        {triggeringReminder ? (
                            <>
                                <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
                                <span>Sending Reminders...</span>
                            </>
                        ) : (
                            <>
                                <Send size={14} />
                                <span>⏰ Trigger 12-Hour Reminder Now</span>
                            </>
                        )}
                    </button>

                    <Link
                        to={`/invite/whatsapp/settings?campaignId=${campaign._id}`}
                        style={{
                            color: isLightTheme ? '#334155' : '#CBD5E1',
                            textDecoration: 'none',
                            fontSize: '0.82rem',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '8px 14px',
                            background: isLightTheme ? '#FFFFFF' : 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '10px',
                            border: isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.12)'
                        }}
                    >
                        📱 Re-link / Check WhatsApp Status
                    </Link>
                </div>
            </div>

            {/* Downloadable Invite Card & Poster */}
            <div className="campaign-detail-card" style={{ marginBottom: '2.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <h3 className="campaign-detail-card-title" style={{ alignSelf: 'flex-start', marginBottom: '1.25rem' }}>🎨 Downloadable WhatsApp Card &amp; Poster</h3>
                <InviteCardGenerator 
                    eventName={campaign.eventName}
                    eventType={campaign.eventType}
                    eventDate={campaign.eventDate}
                    eventVenue={campaign.eventVenue}
                    eventCity={campaign.eventCity}
                    rsvpLink={window.location.origin + `/invite/rsvp/sample-token`}
                />
            </div>

            {/* Guest List Detail Table */}
            <div className="campaign-detail-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
                    <div>
                        <h3 className="campaign-detail-card-title">Invited Guests &amp; RSVP Responses</h3>
                        <p className="campaign-detail-card-subtitle">Real-time guest confirmation tracking, catering headcounts, and delivery status.</p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <button
                            onClick={() => fetchDetails(false)}
                            disabled={refreshing}
                            className="btn-refresh-status"
                        >
                            <RefreshCw size={12} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                            Refresh List
                        </button>
                    </div>
                </div>

                {/* RSVP Status Filter Pills */}
                <div className="rsvp-filter-pills-row">
                    {[
                        { id: 'all', label: 'All Guests', count: recipients.length, color: isLightTheme ? '#475569' : '#94A3B8' },
                        { id: 'accepted', label: '✅ Attending', count: recipients.filter(r => r.rsvpStatus === 'accepted').length, color: isLightTheme ? '#059669' : '#34D399' },
                        { id: 'maybe', label: '❓ Tentative', count: recipients.filter(r => r.rsvpStatus === 'maybe').length, color: isLightTheme ? '#D97706' : '#FBBF24' },
                        { id: 'declined', label: '❌ Declined', count: recipients.filter(r => r.rsvpStatus === 'declined').length, color: isLightTheme ? '#DC2626' : '#F87171' },
                        { id: 'pending', label: '⏳ Awaiting Reply', count: recipients.filter(r => !r.rsvpStatus || r.rsvpStatus === 'pending').length, color: isLightTheme ? '#64748B' : '#CBD5E1' },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setRsvpFilter(tab.id as any)}
                            className={`rsvp-filter-pill-btn ${rsvpFilter === tab.id ? 'active' : ''}`}
                            style={{
                                color: rsvpFilter === tab.id ? tab.color : undefined,
                                borderColor: rsvpFilter === tab.id ? tab.color : undefined,
                            }}
                        >
                            {tab.label} ({tab.count})
                        </button>
                    ))}
                </div>

                <div className="preview-table-wrapper">
                    <table className="recipients-table">
                        <thead>
                            <tr>
                                <th>Guest Details</th>
                                <th>Contact</th>
                                <th>RSVP &amp; Headcount</th>
                                <th>Catering &amp; Stay</th>
                                <th>Wishes / Notes</th>
                                <th>Delivery Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRecipients.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="empty-filter-cell">
                                        No guests match the selected filter.
                                    </td>
                                </tr>
                            ) : (
                                filteredRecipients.map(rec => (
                                    <tr key={rec._id}>
                                        {/* 1. Name & Salutation & Relationship */}
                                        <td>
                                            <div className="guest-name-text">
                                                {rec.customFields?.salutation && (
                                                    <span className="guest-salutation-tag">{rec.customFields.salutation}</span>
                                                )}
                                                {rec.name || 'Guest'}
                                            </div>
                                            {(rec.customFields?.relationship || rec.customFields?.familySide) && (
                                                <span className="guest-relationship-tag">
                                                    {[rec.customFields.relationship, rec.customFields.familySide].filter(Boolean).join(' · ')}
                                                </span>
                                            )}
                                        </td>

                                        {/* 2. Contact (Phone & Email) */}
                                        <td>
                                            <div className="guest-contact-phone">{rec.phone || '-'}</div>
                                            {rec.email && (
                                                <div className="guest-contact-email">{rec.email}</div>
                                            )}
                                        </td>

                                        {/* 3. RSVP Status & Headcount */}
                                        <td>
                                            <span style={{
                                                padding: '4px 10px',
                                                borderRadius: '6px',
                                                fontSize: '0.78rem',
                                                fontWeight: 700,
                                                display: 'inline-block',
                                                background: rec.rsvpStatus === 'accepted' 
                                                    ? (isLightTheme ? '#ECFDF5' : 'rgba(16, 185, 129, 0.15)') 
                                                    : rec.rsvpStatus === 'declined' 
                                                    ? (isLightTheme ? '#FEF2F2' : 'rgba(239, 68, 68, 0.15)') 
                                                    : rec.rsvpStatus === 'maybe' 
                                                    ? (isLightTheme ? '#FFFBEB' : 'rgba(245, 158, 11, 0.15)') 
                                                    : (isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.06)'),
                                                color: rec.rsvpStatus === 'accepted' 
                                                    ? (isLightTheme ? '#059669' : '#34D399') 
                                                    : rec.rsvpStatus === 'declined' 
                                                    ? (isLightTheme ? '#DC2626' : '#F87171') 
                                                    : rec.rsvpStatus === 'maybe' 
                                                    ? (isLightTheme ? '#D97706' : '#FBBF24') 
                                                    : (isLightTheme ? '#64748B' : '#94A3B8'),
                                                border: `1px solid ${
                                                    rec.rsvpStatus === 'accepted' 
                                                        ? (isLightTheme ? '#A7F3D0' : 'rgba(16, 185, 129, 0.3)') 
                                                        : rec.rsvpStatus === 'declined' 
                                                        ? (isLightTheme ? '#FECACA' : 'rgba(239, 68, 68, 0.3)') 
                                                        : rec.rsvpStatus === 'maybe' 
                                                        ? (isLightTheme ? '#FDE68A' : 'rgba(245, 158, 11, 0.3)') 
                                                        : (isLightTheme ? '#CBD5E1' : 'rgba(255, 255, 255, 0.1)')
                                                }`
                                            }}>
                                                {rec.rsvpStatus === 'accepted' ? '✓ Attending' : rec.rsvpStatus === 'declined' ? '✗ Declined' : rec.rsvpStatus === 'maybe' ? '? Maybe' : '⏳ Awaiting'}
                                            </span>

                                            {rec.rsvpStatus === 'accepted' && (
                                                <div className="guest-headcount-badge">
                                                    {rec.adultsCount || 1} Adult{(rec.adultsCount || 1) > 1 ? 's' : ''}{rec.kidsCount ? `, ${rec.kidsCount} Kid${rec.kidsCount > 1 ? 's' : ''}` : ''} ({rec.attendeesCount || 1} Total)
                                                </div>
                                            )}
                                        </td>

                                        {/* 4. Catering & Stay */}
                                        <td>
                                            {rec.rsvpStatus === 'accepted' ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                    <span className="guest-dietary-text">
                                                        {rec.dietaryPreference === 'vegetarian' ? '🌱 Vegetarian' :
                                                         rec.dietaryPreference === 'non-vegetarian' ? '🍗 Non-Veg' :
                                                         rec.dietaryPreference === 'jain' ? '🥗 Jain' :
                                                         rec.dietaryPreference === 'vegan' ? '🥑 Vegan' :
                                                         rec.dietaryPreference === 'eggetarian' ? '🥚 Eggetarian' : '🍽️ Standard'}
                                                    </span>
                                                    {rec.needsAccommodation && (
                                                        <span className="guest-stay-badge">
                                                            🏨 Stay Needed {rec.arrivalDate ? `(${rec.arrivalDate})` : ''}
                                                        </span>
                                                    )}
                                                    {rec.transportDetails && (
                                                        <span style={{ fontSize: '0.72rem', color: isLightTheme ? '#7C3AED' : '#C084FC', fontWeight: 600 }}>
                                                            ✈️ {rec.transportDetails}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="guest-empty-dash">-</span>
                                            )}
                                        </td>

                                        {/* 5. Wishes / Notes */}
                                        <td style={{ maxWidth: '200px' }}>
                                            {rec.rsvpNote ? (
                                                <span className="guest-wishes-quote" title={rec.rsvpNote}>
                                                    "{rec.rsvpNote.length > 50 ? rec.rsvpNote.slice(0, 47) + '...' : rec.rsvpNote}"
                                                </span>
                                            ) : (
                                                <span className="guest-empty-dash">-</span>
                                            )}
                                        </td>

                                        {/* 6. Channel Delivery Status */}
                                        <td>
                                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                                                {campaign.channels.map(ch => {
                                                    const stat = rec.deliveryStatus?.[ch];
                                                    const statusVal = stat?.status || 'pending';

                                                    let badgeBg = isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.05)';
                                                    let badgeColor = isLightTheme ? '#64748B' : '#94A3B8';
                                                    let badgeBorder = isLightTheme ? '#CBD5E1' : 'rgba(255, 255, 255, 0.08)';
                                                    let icon = <Clock size={11} />;

                                                    if (statusVal === 'sent') {
                                                        badgeBg = isLightTheme ? '#ECFDF5' : 'rgba(16, 185, 129, 0.15)';
                                                        badgeColor = isLightTheme ? '#059669' : '#34D399';
                                                        badgeBorder = isLightTheme ? '#A7F3D0' : 'rgba(16, 185, 129, 0.3)';
                                                        icon = <CheckCircle2 size={11} />;
                                                    } else if (statusVal === 'sending') {
                                                        badgeBg = isLightTheme ? '#FFFBEB' : 'rgba(245, 158, 11, 0.15)';
                                                        badgeColor = isLightTheme ? '#D97706' : '#FBBF24';
                                                        badgeBorder = isLightTheme ? '#FDE68A' : 'rgba(245, 158, 11, 0.3)';
                                                        icon = <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />;
                                                    } else if (statusVal === 'failed') {
                                                        badgeBg = isLightTheme ? '#FEF2F2' : 'rgba(239, 68, 68, 0.15)';
                                                        badgeColor = isLightTheme ? '#DC2626' : '#F87171';
                                                        badgeBorder = isLightTheme ? '#FECACA' : 'rgba(239, 68, 68, 0.3)';
                                                        icon = <XCircle size={11} />;
                                                    }

                                                    return (
                                                        <span
                                                            key={ch}
                                                            title={stat?.error || (statusVal === 'sent' ? `Sent at ${stat?.sentAt || 'recent'}` : statusVal)}
                                                            style={{
                                                                fontSize: '0.72rem',
                                                                padding: '2px 8px',
                                                                borderRadius: '4px',
                                                                background: badgeBg,
                                                                color: badgeColor,
                                                                border: `1px solid ${badgeBorder}`,
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '0.25rem',
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {icon}
                                                            {ch.toUpperCase()}: {statusVal}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default InviteCampaignDetail;
